// Tells an external barcode scanner apart from a person typing. A scanner is a keyboard that types
// a whole code in one burst - tens of milliseconds between characters, usually ending with Enter -
// while people on a touch keyboard are several times slower.

export type ScanBurstDetectorOptions = {
    /** Longest pause between two characters of one scan. */
    maxGapMs?: number;
    /** Shortest burst that counts as a scan. */
    minLength?: number;
    /** Quiet time that ends a scan from a scanner configured without Enter. */
    idleMs?: number;
    now?: () => number;
};

export class ScanBurstDetector {
    private readonly maxGapMs: number;
    private readonly minLength: number;
    private readonly idleMs: number;
    private readonly now: () => number;

    private field: string | null = null;
    private readonly lengths: Record<string, number> = {};
    private lastAt = 0;
    private count = 0;
    private value = '';
    private idleTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(private readonly onScan: (field: string, value: string) => void, options: ScanBurstDetectorOptions = {}) {
        this.maxGapMs = options.maxGapMs ?? 100;
        this.minLength = options.minLength ?? 4;
        this.idleMs = options.idleMs ?? 150;
        this.now = options.now ?? (() => Date.now());
    }

    /** Feed every input event of a scannable field, with the field's value after the change. */
    input(field: string, value: string): void {
        const at = this.now();
        const previousLength = this.lengths[field];
        // One typed character grows this field's value by one. A field seen for the first time has no
        // earlier length, so its first change is taken as a typed character.
        const grewByOne = previousLength === undefined ? value.length >= 1 : value.length === previousLength + 1;

        if (field === this.field && grewByOne && at - this.lastAt < this.maxGapMs) {
            this.count += 1;
        } else {
            this.finish();
            // A single character always starts a burst, whatever the stored length: values set in
            // code (a camera scan, clearing a wrong prefix) change the field without an input event,
            // so that length can be stale. It never extends a running burst, so deleting down to
            // one character still breaks the series.
            this.count = grewByOne || value.length === 1 ? 1 : 0;
        }

        this.lengths[field] = value.length;
        this.field = field;
        this.lastAt = at;
        this.value = value;
        this.restartIdleTimer();
    }

    /** Feed Enter pressed in a scannable field - the usual end of a scan. */
    enter(field: string): void {
        if (field === this.field) {
            this.finish();
        }
    }

    /** Drop the pending idle timer, e.g. when the form unmounts. */
    dispose(): void {
        this.clearIdleTimer();
        this.count = 0;
    }

    private finish(): void {
        this.clearIdleTimer();
        const count = this.count;
        // Reset before notifying, so a throwing callback cannot leave the burst counted.
        this.count = 0;
        if (this.field !== null && count >= this.minLength) {
            this.onScan(this.field, this.value);
        }
    }

    private restartIdleTimer(): void {
        this.clearIdleTimer();
        this.idleTimer = setTimeout(() => this.finish(), this.idleMs);
    }

    private clearIdleTimer(): void {
        if (this.idleTimer !== null) {
            clearTimeout(this.idleTimer);
            this.idleTimer = null;
        }
    }
}
